import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Timer, ShoppingBag, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { api } from "../lib/api";

const DROP_DATE = new Date("2026-12-01T18:00:00Z");
const PRODUCTS_LIVE = false;

const L = {
  de: { kicker: "Drop 01 · Vorbestellung", title: "Signature Linie 01", sub: "Limitiert, vorbestellbar, in Premium-Qualität. Produziert wird erst ab genug Vorbestellungen – nachhaltig & lagerfrei.", countdown: "Nächster Drop-Stichtag", pre: "Vorbestellen", days: "Tage", hrs: "Std", min: "Min", sec: "Sek", note: "Testmodus: Zahlung mit Stripe-Testkarte 4242 4242 4242 4242.", soon: "Die Produkte werden gerade finalisiert. Trag dich in den Newsletter ein und sei beim Drop als Erste:r dabei.", soonBtn: "Newsletter abonnieren" },
  en: { kicker: "Drop 01 · Pre-order", title: "Signature Line 01", sub: "Limited, pre-orderable, premium quality. Produced only once enough pre-orders are in – sustainable & stock-free.", countdown: "Next drop deadline", pre: "Pre-order", days: "days", hrs: "hrs", min: "min", sec: "sec", note: "Test mode: pay with Stripe test card 4242 4242 4242 4242.", soon: "The products are being finalised. Join the newsletter to be first in line for the drop.", soonBtn: "Subscribe to newsletter" },
};

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
  const diff = Math.max(0, target.getTime() - now);
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

export default function Drops() {
  const { lang } = useLang();
  const t = L[lang];
  const [pkgs, setPkgs] = useState([]);
  const [busy, setBusy] = useState("");
  const cd = useCountdown(DROP_DATE);

  useEffect(() => { api.get("/drops/packages").then((r) => setPkgs(r.data)).catch(() => {}); }, []);

  const preorder = async (id) => {
    setBusy(id);
    try {
      const r = await api.post("/payments/checkout", { package_id: id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch { toast.error("Fehler / Error"); setBusy(""); }
  };

  const box = (v, l) => (
    <div className="text-center"><div className="font-mono text-3xl md:text-4xl text-sonne">{String(v).padStart(2, "0")}</div><div className="instrument-label text-wachs-dim">{l}</div></div>
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:py-16">
      <div className="flex items-center gap-2 instrument-label text-sonne"><Sparkles size={16} /> {t.kicker}</div>
      <h1 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight">{t.title}</h1>
      <p className="mt-4 text-wachs-dim max-w-2xl leading-relaxed">{t.sub}</p>

      <div className="mt-8 grid-panel rounded-lg p-6">
        <div className="flex items-center gap-2 instrument-label text-wachs-dim"><Timer size={15} /> {t.countdown}</div>
        <div className="mt-4 flex gap-6">{box(cd.d, t.days)}{box(cd.h, t.hrs)}{box(cd.m, t.min)}{box(cd.s, t.sec)}</div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {PRODUCTS_LIVE && pkgs.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            data-testid={`drop-${p.id}`} className="grid-panel rounded-lg overflow-hidden">
            <div className="aspect-[16/9] bg-gradient-to-br from-tiefsee-2 to-nacht flex items-center justify-center">
              <ShoppingBag size={40} className="text-bronze/50" />
            </div>
            <div className="p-5">
              <h3 className="font-serif text-xl">{p.name}</h3>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-mono text-lg text-sonne">€ {p.amount.toFixed(2)}</span>
                <button onClick={() => preorder(p.id)} disabled={busy === p.id} data-testid={`preorder-${p.id}`}
                  className="inline-flex items-center gap-2 bg-bronze text-nacht font-medium px-5 py-2.5 rounded-md hover:bg-bronze-soft transition-colors disabled:opacity-50">
                  {busy === p.id ? <Loader2 size={16} className="animate-spin" /> : <ShoppingBag size={16} />} {t.pre}
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {PRODUCTS_LIVE ? (
        <p className="mt-6 font-mono text-xs text-wachs-dim/70">{t.note}</p>
      ) : (
        <div className="mt-8 grid-panel rounded-lg p-8 text-center" data-testid="drops-coming-soon">
          <ShoppingBag size={40} className="mx-auto text-bronze/50" />
          <p className="mt-4 text-wachs-dim max-w-lg mx-auto">{t.soon}</p>
          <Link to="/#newsletter" className="mt-5 inline-block bg-sonne text-nacht font-medium px-6 py-3 rounded-md hover:opacity-90 transition-opacity">
            {t.soonBtn}
          </Link>
        </div>
      )}
    </div>
  );
}
